import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ButtonGroup, Button } from 'reactstrap';
import { Loader, PageTitle, Planner, SectorSelector } from '../../components';
import { loadShifts, getShiftsLinkedResources } from '../../redux';
import { Utils } from '../../services';
import { ApplicationType, Phase, Sector, Activity } from '../../models';
import moment from 'moment';

import './Plan.css';

const now = moment().hours(8).toDate();

const Plan = (props) => {
    const dispatch    = useDispatch();
    const { t, i18n } = useTranslation();

    const [startDate, setStartDate]                        = useState(now);
    const [selectedApplicationType, selectApplicationType] = useState(null);
    const [selectedPhase, selectPhase]                     = useState(null);
    const [selectedSectorId, selectSector]                 = useState(null);

    const { shifts, linkedResources } = useSelector(state => state.shifts);
    const applicationTypes = React.useMemo(() => {
        return (linkedResources?.application_types || []).map(p => new ApplicationType(p));
    }, [linkedResources?.phases]);
    const phases = React.useMemo(() => {
        return (linkedResources?.phases || [])
            .filter(p => p.application_type_id === selectedApplicationType?.id)
            .map(p => new Phase(p));
    }, [linkedResources?.phases, selectedApplicationType]);
    const sectors = React.useMemo(() => {
        return (linkedResources?.sectors || []).map(s => new Sector(s));
    }, [linkedResources?.sectors]);
    const activities = React.useMemo(() => {
        return (linkedResources?.activities || []).map(a => new Activity(a));
    }, [linkedResources?.activities]);

    useEffect(() => {
        dispatch(getShiftsLinkedResources());
    }, []);

    useEffect(() => {
        if (applicationTypes?.length)
            selectApplicationType(applicationTypes[0]);
    }, [applicationTypes]);

    useEffect(() => {
        if (selectedApplicationType)
            dispatch(loadShifts({
                application_types_ids: [selectedApplicationType.id]
            }));
    }, [selectedApplicationType?.id]);

    useEffect(() => {
        if (phases?.length) {
            // let's select the closest current or upcoming phase
            phases.sort((a, b) => a.start_at - b.start_at);
            let index = 0;
            const now = new Date();
            while((index < phases.length - 1) && phases[index].stop_at < now) {
                index ++;
            }
            selectPhase(phases[index]);
        }
    }, [phases]);

    useEffect(() => {
        if (selectedPhase)
            setStartDate(selectedPhase.start_at.toDate());
    }, [ selectedPhase ]);

    const title = <span>{t("plan.title")}</span>;

    const actions = (applicationTypes?.length > 1) ? (
        <ButtonGroup className="w-100">
            { applicationTypes.map(applicationType => (
                <Button
                    key={ applicationType.id }
                    color={ applicationType.id === selectedApplicationType?.id ? 'primary' : 'dark'}
                    onClick={ () => selectApplicationType(applicationType) }
                >
                    <i className="fa fa-address-book" />&nbsp;
                    { Utils.localized_or_fallback(applicationType.name, i18n.language) }
                </Button>
            ))}
        </ButtonGroup>
    ) : null;

    const tree = React.useMemo(() => Sector.buildTree(sectors), [ sectors ]);

    const children = React.useMemo(() => {
        const children = [];
        const add = sector => {
            children.push(sector.id);
            sector.children.forEach(s => add(s));
        };

        const searchIn = sector => {
            if (sector.id === selectedSectorId)
                sector.children.forEach(s => add(s));
            else
                sector.children.forEach(s => searchIn(s));
        };

        tree.forEach(sector => searchIn(sector));

        return children;
    }, [selectedSectorId]);

    const resources = React.useMemo(() => sectors?.sort((a, b) => {
        return Utils.localized_or_fallback(a.name, i18n.language).localeCompare(
            Utils.localized_or_fallback(b.name, i18n.language)
        );
    }).filter(sector => {
        if (!selectedSectorId)
            return true;

        return selectedSectorId === sector.id || children?.includes(sector.id);
    }).map(sector => ({
        id: sector.id,
        title: Utils.localized_or_fallback(sector.name, i18n.language)
    })), [ sectors, selectedSectorId, children, i18n.language ]);

    const tasks = shifts?.map(shift => ({
        id: shift.id,
        start: shift.start_at.toDate(),
        end: shift.stop_at.toDate(),
        resourceId: shift.sector_id,
        title: `${shift.nb_volunteers} / ${shift.nb_volunteers}`,
        shift,
    }));

    const isLoaded = !!selectedApplicationType && !!selectedPhase;
    return (
        <div className="Plan">
            <PageTitle icon="calendar-plus-o" title={title} actions={actions} />
            {!isLoaded ? (
                <Loader />
            ) : (
                <>
                    <div className="my-2 text-center">
                        {phases && (
                            <ButtonGroup className="w-100">
                            { phases.map(phase => (
                                <Button
                                    key={ phase.id }
                                    color={ phase.id === selectedPhase?.id ? 'primary' : 'dark'}
                                    onClick={ () => selectPhase(phase) }
                                >
                                    <h3>
                                        { Utils.localized_or_fallback(phase.name, i18n.language) }
                                    </h3>
                                    <div>{ t('plan.from') } { phase.start_at.format('LL') }</div>
                                    <div>{ t('plan.to') } { phase.stop_at.format('LL') }</div>
                                </Button>
                            ))}
                            </ButtonGroup>
                        )}
                        <hr />
                        { sectors && (
                        <SectorSelector
                            sectors={ sectors }
                            placeholder={ t('plan.select_sector') }
                            onChange={ (e, data) => selectSector(data.value) }
                            single
                        />
                        )}

                    </div>
                    <Planner
                        applicationType={ selectedApplicationType }
                        sectors={ sectors }
                        activities={ activities }
                        resources={ resources }
                        tasks={ tasks }
                        currentDate={ startDate }
                    />
                </>
            )}
        </div>
    );
}

export default Plan;
